import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import styled from "styled-components";
import { Tooltip as InfoTooltip } from "react-tooltip";
import { Info } from "lucide-react";

const ChartWrapper = styled.div`
  position: relative;
  background-color: #f9f9f9;
  width: 100%;
  height: 40vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 15px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
`;

const WeekDetailsChart = ({ data }) => {
  const [chartData, setChartData] = useState(null);
  const [error, setError] = useState(null);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);

  const obliczSprzedaz = (row) =>
    Math.max(
      0,
      parseFloat(
        row.K_15 ||
          row.K_19 ||
          row.K_10 ||
          row.K_21 ||
          row.K_17 ||
          row.K_11 ||
          row.K_22
      ) || 0
    );

  const getWeekNumber = (date) => {
    const currentDate = new Date(date);
    if (isNaN(currentDate)) return null;
    const startOfYear = new Date(currentDate.getFullYear(), 0, 1);
    const days = Math.floor((currentDate - startOfYear) / (24 * 60 * 60 * 1000));
    return Math.ceil((days + startOfYear.getDay() + 1) / 7);
  };

  useEffect(() => {
    try {
      if (data && data.length > 0) {
        const years = Array.from(
          new Set(data.map((row) => parseInt(row.Rok, 10)))
        ).sort((a, b) => a - b);
        setAvailableYears(years);
        const mostRecentYear = Math.max(...years);
        if (!selectedYear) setSelectedYear(mostRecentYear);
      }
    } catch (e) {
      setError("Błąd podczas wczytywania listy lat.");
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }, [data]);

  useEffect(() => {
    try {
      if (selectedYear) {
        const weeks = new Set();
        data.forEach((row) => {
          const year = parseInt(row.Rok, 10);
          if (year === selectedYear) {
            const week = getWeekNumber(row.DataWystawienia);
            if (week) weeks.add(week);
          }
        });

        const sortedWeeks = Array.from(weeks).sort((a, b) => a - b);
        setAvailableWeeks(sortedWeeks);

        if (!selectedWeek && sortedWeeks.length > 0) {
          setSelectedWeek(sortedWeeks[0]);
        }
      }
    } catch (e) {
      setError("Błąd podczas wczytywania listy tygodni.");
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }, [selectedYear, data, selectedWeek]);

  useEffect(() => {
    if (!selectedWeek || !selectedYear) return;

    const getSalesForWeek = (year) => {
      const filtered = data.filter((row) => {
        const rowYear = parseInt(row.Rok, 10);
        return (
          rowYear === year &&
          getWeekNumber(row.DataWystawienia) === selectedWeek &&
          obliczSprzedaz(row) > 0
        );
      });

      const dailyData = filtered.reduce((acc, row) => {
        const date = new Date(row.DataWystawienia);
        const day = date.getDay(); // 0..6 (Sun..Sat)
        const weekdayIndex = (day + 6) % 7; // Monday=0..Sunday=6
        if (!acc[weekdayIndex]) acc[weekdayIndex] = 0;
        acc[weekdayIndex] += obliczSprzedaz(row);
        return acc;
      }, {});

      return Array.from({ length: 7 }, (_, i) => dailyData[i] || 0);
    };

    const currentSales = getSalesForWeek(selectedYear);
    const previousSales = getSalesForWeek(selectedYear - 1);

    // Colors per your convention (no red max highlight)
    const GREEN = "#2e7d32";   // current-year bars
    const ORANGE = "#fb8c00";  // previous-year bars

    setChartData({
      labels: ["Pon", "Wt", "Śr", "Czw", "Pt", "Sob", "Nd"],
      datasets: [
        {
          label: `Sprzedaż (${selectedYear})`,
          data: currentSales,
          type: "bar",
          backgroundColor: "rgba(46, 125, 50, 0.70)",
          borderColor: GREEN,
          borderWidth: 1,
        },
        {
          label: `Sprzedaż (${selectedYear - 1})`,
          data: previousSales,
          type: "bar",
          backgroundColor: "rgba(251, 140, 0, 0.55)",
          borderColor: ORANGE,
          borderWidth: 1,
        },
      ],
    });
  }, [selectedWeek, selectedYear, data]);

  if (error) return <p style={{ color: "red" }}>{error}</p>;
  if (!chartData) return <p>Ładowanie danych...</p>;

  return (
    <ChartWrapper>
      {/* Info icon and tooltip */}
      <InfoIconWrapper data-tooltip-id="week-details-tooltip">
        <Info />
      </InfoIconWrapper>

      <InfoTooltip
        id="week-details-tooltip"
        place="left"
        content={
          <>
            <strong>Sprzedaż według dnia tygodnia</strong>
            <br />
            Porównanie sum sprzedaży w dniach tygodnia dla
            <br />
            wybranego tygodnia: {selectedYear} vs {selectedYear - 1}.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label htmlFor="year-select">Rok:</label>
          <select
            id="year-select"
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="week-select">Tydzień:</label>
          <select
            id="week-select"
            value={selectedWeek || ""}
            onChange={(e) => setSelectedWeek(parseInt(e.target.value, 10))}
          >
            {availableWeeks.map((week) => (
              <option key={week} value={week}>
                {week}
              </option>
            ))}
          </select>
        </div>
      </FiltersWrapper>

      <Bar
        data={chartData}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              stacked: false,
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: "Sprzedaż",
              },
              ticks: {
                callback: function (value) {
                  return Number(value)
                    .toFixed(2)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
                },
              },
            },
          },
          plugins: {
            title: {
              display: true,
              text: `Sprzedaż według dnia tygodnia – Tydzień ${selectedWeek}`,
            },
            legend: {
              display: true,
            },
            tooltip: {
              callbacks: {
                label: function (context) {
                  const label = context.dataset.label || "";
                  const value = context.parsed.y || 0;
                  const formattedValue = Number(value)
                    .toFixed(2)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
                  return `${label}: ${formattedValue}`;
                },
              },
            },
          },
        }}
      />
    </ChartWrapper>
  );
};

export default WeekDetailsChart;
